import React, { useState, useEffect } from 'react';
import { useAuth } from './contexts/AuthContext';
import { useTranslation } from 'react-i18next';
import env from './utils/env';
import TypeaheadDropdown from './components/TypeaheadDropdown';
import Select from './components/Select';
import { useDebounce } from './hooks/useDebounce';
import { useReportExperimentsFilterOptions } from './hooks/useFilterOptions';
import './ReportExperimentsView.css';

interface ValidationResultField {
  field: string;
  value: any;
  found: boolean;
  confidence: number;
  matches: any[];
}

interface ValidationResults {
  [key: string]: ValidationResultField;
}

interface LogProbItem {
  bytes: number[];
  logprob: number;
  token: string;
  top_logprobs: any[];
}

interface UsageData {
  completion_tokens: number;
  completion_tokens_details?: {
    accepted_prediction_tokens: number;
    audio_tokens: number;
    reasoning_tokens: number;
    rejected_prediction_tokens: number;
  };
  prompt_tokens: number;
  prompt_tokens_details?: {
    audio_tokens: number;
    cached_tokens: number;
  };
  total_tokens: number;
}

interface MetaData {
  num_tokens: number;
  cut: boolean;
}

interface ReportExperiment {
  _id: string;
  experiment_id: string | number;
  method_name?: string;
  report_source_ref: string;
  experiment_storage_id?: string;
  filename: string;
  update_timestamp: string;
  language?: string;
  extracted_results: {
    plan?: string;
    category?: string[];
    class_probabilities?: number[];
    short_description?: string;
    reasoning?: string[];
    title?: string;
    author?: string;
    confidence?: number;
    [key: string]: any;
  };
  log_probs?: LogProbItem[];
  meta?: MetaData;
  raw_result?: string;
  thinking_time?: number;
  usage?: UsageData;
  processing_time_seconds?: number;
  processing_timestamp: string;
  provider?: string;
  fields_found?: number;
  total_fields_validated?: number;
  validation_passed?: boolean;
  validation_results?: ValidationResults;
  validation_score?: number;
  validation_timestamp?: string;
  model?: string;
  tags?: string[];
}

interface ReportExperimentsResponse {
  report_experiments: ReportExperiment[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

const ReportExperimentsView: React.FC = () => {
  const { token } = useAuth();
  const { t } = useTranslation();
  const [reportExperiments, setReportExperiments] = useState<ReportExperiment[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [filterExperimentId, setFilterExperimentId] = useState<string>('');
  const [filterProvider, setFilterProvider] = useState<string>('');
  const [filterModel, setFilterModel] = useState<string>('');
  const [filterTags, setFilterTags] = useState<string>('');
  const [filterCategory, setFilterCategory] = useState<string>('');
  const [filterExperimentType, setFilterExperimentType] = useState<string>('');
  const [selectedReportExperiment, setSelectedReportExperiment] = useState<ReportExperiment | null>(null);
  const [showDetailView, setShowDetailView] = useState(false);
  const apiBaseUrl = env.apiUrl;
  
  // Use optimized filter options hook with caching and preloading
  const { filterOptions: filterOptionsData } = useReportExperimentsFilterOptions();
  
  // Extract filter options for backward compatibility
  const availableExperimentIds = filterOptionsData.experimentIds || [];
  const availableProviders = filterOptionsData.providers || [];
  const availableModels = filterOptionsData.models || [];
  const availableTags = filterOptionsData.tags || [];
  const availableCategories = filterOptionsData.categories || [];
  const availableExperimentTypes = filterOptionsData.experimentTypes || [];
  
  // Debounced filter values
  const debouncedExperimentId = useDebounce(filterExperimentId, 300);
  const debouncedProvider = useDebounce(filterProvider, 300);
  const debouncedModel = useDebounce(filterModel, 300);
  const debouncedTags = useDebounce(filterTags, 300);
  const debouncedCategory = useDebounce(filterCategory, 300);
  const debouncedExperimentType = useDebounce(filterExperimentType, 300);

  // Helper function to get confidence from extracted results
  const getExtractedResultsConfidence = (reportExperiment: ReportExperiment): number | null => {
    if (!reportExperiment.extracted_results) {
      return null;
    }
    
    // Look for confidence field in extracted_results
    if (typeof reportExperiment.extracted_results.confidence === 'number') {
      return reportExperiment.extracted_results.confidence;
    }
    
    // Look for average_confidence field in extracted_results
    if (typeof reportExperiment.extracted_results.average_confidence === 'number') {
      return reportExperiment.extracted_results.average_confidence;
    }
    
    // Look for overall_confidence field in extracted_results
    if (typeof reportExperiment.extracted_results.overall_confidence === 'number') {
      return reportExperiment.extracted_results.overall_confidence;
    }
    
    return null;
  };

  const fetchReportExperiments = async (page: number = 1) => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams({
        page: page.toString(),
        limit: '20',
      });
      if (debouncedExperimentId) params.append('experiment_id', debouncedExperimentId);
      if (debouncedProvider) params.append('provider', debouncedProvider);
      if (debouncedModel) params.append('model', debouncedModel);
      if (debouncedTags) params.append('tags', debouncedTags);
      if (debouncedCategory) params.append('category', debouncedCategory);
      if (debouncedExperimentType) params.append('type', debouncedExperimentType);
      const response = await fetch(`${apiBaseUrl}/admin/report-experiments?${params}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });
      if (!response.ok) {
        throw new Error(`Failed to fetch report experiments: ${response.statusText}`);
      }
      const data: ReportExperimentsResponse = await response.json();
      
      // Category filtering is now handled server-side
      setReportExperiments(data.report_experiments);
      setCurrentPage(data.pagination.page);
      setTotalPages(data.pagination.totalPages);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchReportExperiments(currentPage);
    // eslint-disable-next-line
  }, [currentPage, token, debouncedExperimentId, debouncedProvider, debouncedModel, debouncedTags, debouncedCategory, debouncedExperimentType]);

  // Filter options are now automatically fetched by the useReportExperimentsFilterOptions hook

  if (loading && reportExperiments.length === 0) {
    return (
      <div className="container">
        <div className="loading">{t('loading.reportExperiments') || 'Loading report experiments...'}</div>
      </div>
    );
  }

  if (showDetailView && selectedReportExperiment) {
    return (
      <ReportExperimentDetailsPage
        reportExperiment={selectedReportExperiment}
        onClose={() => {
          setShowDetailView(false);
          setSelectedReportExperiment(null);
        }}
      />
    );
  }

  return (
    <div className="container">
      <div className="experiments-header">
        <div className="experiments-stats">
          <span className="stat">{t('experiments.totalExperiments', { count: reportExperiments.length })}</span>
        </div>
        {totalPages > 1 && (
          <div className="pagination">
            <button
              onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
              disabled={currentPage === 1 || loading}
              className="pagination-btn"
            >
              {t('pagination.previous')}
            </button>
            <span className="pagination-info">
            {t('pagination.page')} {currentPage} {t('pagination.of')} {totalPages}
          </span>
            <button
              onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
              disabled={currentPage === totalPages || loading}
              className="pagination-btn"
            >
              {t('pagination.next')}
            </button>
          </div>
        )}
      </div>
      <div className="experiments-controls">
        <div className="filters-header">
          <h3>{t('experiments.filters')}</h3>
          {(filterExperimentId || filterProvider || filterModel || filterTags || filterCategory || filterExperimentType) && (
            <button 
              className="clear-filters-btn"
              onClick={() => {
                setFilterExperimentId('');
                setFilterProvider('');
                setFilterModel('');
                setFilterTags('');
                setFilterCategory('');
                setFilterExperimentType('');
                setCurrentPage(1);
              }}
            >
              {t('experiments.clearAllFilters')}
            </button>
          )}
        </div>
        <div className="filters">
          <TypeaheadDropdown
            options={availableExperimentIds}
            value={filterExperimentId}
            onChange={(value) => { setFilterExperimentId(value); setCurrentPage(1); }}
            placeholder={t('experiments.experimentId')}
          />
                     <Select
             options={availableExperimentTypes}
             value={filterExperimentType}
             onChange={(value) => { setFilterExperimentType(value); setCurrentPage(1); }}
             placeholder={t('experiments.experimentType')}
           />
          <TypeaheadDropdown
            options={availableProviders}
            value={filterProvider}
            onChange={(value) => { setFilterProvider(value); setCurrentPage(1); }}
            placeholder={t('experiments.provider')}
          />
          <Select
            options={availableModels}
            value={filterModel}
            onChange={(value) => { setFilterModel(value); setCurrentPage(1); }}
            placeholder={t('experiments.model')}
          />
          <TypeaheadDropdown
            options={availableTags}
            value={filterTags}
            onChange={(value) => { setFilterTags(value); setCurrentPage(1); }}
            placeholder={t('experiments.tags')}
          />
          <TypeaheadDropdown
            options={availableCategories}
            value={filterCategory}
            onChange={(value) => { setFilterCategory(value); setCurrentPage(1); }}
            placeholder={t('experiments.categories')}
          />
        </div>
      </div>
      <div className="experiments-table-container">
        <table className="experiments-table">
          <thead>
            <tr>
              <th>{t('experiments.experimentId')}</th>
              <th>{t('experiments.method')}</th>
              <th>{t('experiments.provider')}</th>
              <th>{t('experiments.model')}</th>
              <th>{t('experiments.filename')}</th>
              <th>{t('experiments.categories')}</th>
              <th>{t('experiments.tags')}</th>
              <th>{t('experiments.validationPassed')}</th>
              <th>{t('experiments.validationScore')}</th>
              <th>{t('experiments.confidence')}</th>
              <th>{t('experiments.processingTime')}</th>
              <th>{t('experiments.processed')}</th>
            </tr>
          </thead>
          <tbody>
            {reportExperiments.map((exp) => (
              <tr
                key={exp._id}
                className="experiment-row"
                onClick={() => {
                  setSelectedReportExperiment(exp);
                  setShowDetailView(true);
                }}
              >
                <td>{exp.experiment_id}</td>
                <td>{exp.method_name || '-'}</td>
                <td>{exp.provider || '-'}</td>
                <td>{exp.model || '-'}</td>
                <td>{exp.filename}</td>
                <td>{exp.extracted_results?.category && exp.extracted_results.category.length > 0 ? exp.extracted_results.category.join(', ') : '-'}</td>
                <td>{exp.tags && exp.tags.length > 0 ? exp.tags.join(', ') : '-'}</td>
                <td>{exp.validation_passed ? '✅' : exp.validation_passed === false ? '❌' : '-'}</td>
                <td>{typeof exp.validation_score === 'number' ? exp.validation_score.toFixed(2) : '-'}</td>
                <td className="text-center">
                  {(() => {
                    const confidence = getExtractedResultsConfidence(exp);
                    return confidence !== null ? `${(confidence * 100).toFixed(1)}%` : '-';
                  })()}
                </td>
                <td>{exp.processing_time_seconds ? exp.processing_time_seconds.toFixed(2) + 's' : '-'}</td>
                <td>{exp.processing_timestamp ? new Date(exp.processing_timestamp).toLocaleString() : '-'}</td>
              </tr>
            ))}
            <tr>
              <td>&nbsp;</td>
              <td></td>
              <td></td>
              <td></td>
              <td></td>
              <td></td>
              <td></td>
              <td></td>
              <td></td>
              <td></td>
              <td></td>
              <td></td>
            </tr>
          </tbody>
        </table>
        {reportExperiments.length === 0 && (
          <div className="no-data">{t('experiments.noData')}</div>
        )}
      </div>
      {error && (
        <div className="error-message">
          {t('errors.error')} {error}
          <button onClick={() => fetchReportExperiments(currentPage)} className="retry-button">
            {t('buttons.retry')}
          </button>
        </div>
      )}

    </div>
  );
};

// Detail page for a single report experiment
const ReportExperimentDetailsPage: React.FC<{ reportExperiment: ReportExperiment; onClose: () => void }> = ({ reportExperiment, onClose }) => {
  const { t } = useTranslation();
  const [showRaw, setShowRaw] = useState<'full' | 'validation_results' | 'log_probs' | 'raw_result' | null>(null);
  const formatDate = (dateString: string) => dateString ? new Date(dateString).toLocaleString() : '-';
  
  const getConfidenceDisplay = () => {
    const confidence = reportExperiment.extracted_results?.confidence;
    return typeof confidence === 'number' ? `${(confidence * 100).toFixed(1)}%` : '-';
  };

  const getClassProbabilitiesDisplay = () => {
    const probs = reportExperiment.extracted_results?.class_probabilities;
    if (!probs || !Array.isArray(probs)) return '-';
    return probs.map(p => `${(p * 100).toFixed(1)}%`).join(', ');
  };

  return (
    <div className="experiment-detail-fullpage-container">
      <div className="detail-top-spacer"></div>
      
      {/* Unified Header Layout */}
      <div className="detail-header-unified">
        {/* Back Button Row */}
        <div className="detail-header-nav">
          <button className="secondary-button back-button" onClick={onClose}>
            ← Back
          </button>
        </div>
        
        {/* Title and Meta Information */}
        <div className="detail-header-content">
          <div className="detail-title-section">
            <h1 className="detail-title">
              {reportExperiment.extracted_results?.title || reportExperiment.experiment_id || t('experiments.reportExperimentDetails')}
            </h1>
            {reportExperiment.extracted_results?.author && (
              <p className="detail-author">By {reportExperiment.extracted_results.author}</p>
            )}
          </div>
          
          {/* Document Action */}
          <div className="detail-action-section">
            <a 
              href={`/report-sources/${reportExperiment.report_source_ref}`} 
              className="document-action-button"
              target="_blank" 
              rel="noopener noreferrer"
            >
              <span className="material-icons">description</span>
              View Document
              <span className="material-icons">open_in_new</span>
            </a>
            <span className="document-meta">{reportExperiment.filename}</span>
          </div>
        </div>
      </div>

      <div className="detail-content-grid">
        {/* Column 1: Basic Information */}
        <section className="detail-section">
          <h3><span className="material-icons">assignment</span> Basic Information</h3>
          <div className="detail-grid">
            <div className="detail-item">
              <label>Experiment ID:</label>
              <span>{reportExperiment.experiment_id}</span>
            </div>
            <div className="detail-item">
              <label>Document ID:</label>
              <span>{reportExperiment._id}</span>
            </div>
            <div className="detail-item">
              <label>Report Source Ref:</label>
              <span>{reportExperiment.report_source_ref}</span>
            </div>
            <div className="detail-item">
              <label>Storage ID:</label>
              <span>{reportExperiment.experiment_storage_id || '-'}</span>
            </div>
            <div className="detail-item">
              <label>Filename:</label>
              <span>{reportExperiment.filename}</span>
            </div>
            <div className="detail-item">
              <label>Language:</label>
              <span>{reportExperiment.language || '-'}</span>
            </div>
            <div className="detail-item">
              <label>Method:</label>
              <span>{reportExperiment.method_name || '-'}</span>
            </div>
            <div className="detail-item">
              <label>Provider:</label>
              <span>{reportExperiment.provider || '-'}</span>
            </div>
            <div className="detail-item">
              <label>Model:</label>
              <span>{reportExperiment.model || '-'}</span>
            </div>
            <div className="detail-item">
              <label>Tags:</label>
              <span>{reportExperiment.tags && reportExperiment.tags.length > 0 ? reportExperiment.tags.join(', ') : 'None'}</span>
            </div>
          </div>
        </section>

        {/* Column 2: Classification Results */}
        <section className="detail-section">
          <h3><span className="material-icons">track_changes</span> Classification Results</h3>
          <div className="detail-grid">
            <div className="detail-item">
              <label>Categories:</label>
              <span>{reportExperiment.extracted_results?.category && reportExperiment.extracted_results.category.length > 0 ? reportExperiment.extracted_results.category.join(', ') : 'None'}</span>
            </div>
            <div className="detail-item">
              <label>Class Probabilities:</label>
              <span>{getClassProbabilitiesDisplay()}</span>
            </div>
            <div className="detail-item">
              <label>Overall Confidence:</label>
              <span>{getConfidenceDisplay()}</span>
            </div>
            <div className="detail-item">
              <label>Validation Passed:</label>
              <span>{reportExperiment.validation_passed ? '✅' : reportExperiment.validation_passed === false ? '❌' : '-'}</span>
            </div>
            <div className="detail-item">
              <label>Validation Score:</label>
              <span>{typeof reportExperiment.validation_score === 'number' ? reportExperiment.validation_score.toFixed(2) : '-'}</span>
            </div>
            <div className="detail-item">
              <label>Fields Found:</label>
              <span>{reportExperiment.fields_found || '-'}</span>
            </div>
            <div className="detail-item">
              <label>Total Fields Validated:</label>
              <span>{reportExperiment.total_fields_validated || '-'}</span>
            </div>
          </div>
        </section>

        {/* Column 3: Performance Metrics */}
        <section className="detail-section">
          <h3><span className="material-icons">speed</span> Performance Metrics</h3>
          <div className="detail-grid">
            <div className="detail-item">
              <label>Processing Time:</label>
              <span>{reportExperiment.processing_time_seconds ? reportExperiment.processing_time_seconds.toFixed(2) + 's' : '-'}</span>
            </div>
            <div className="detail-item">
              <label>Thinking Time:</label>
              <span>{reportExperiment.thinking_time ? reportExperiment.thinking_time.toFixed(2) + 's' : '-'}</span>
            </div>
            <div className="detail-item">
              <label>Processing Date:</label>
              <span>{formatDate(reportExperiment.processing_timestamp)}</span>
            </div>
            <div className="detail-item">
              <label>Update Date:</label>
              <span>{formatDate(reportExperiment.update_timestamp)}</span>
            </div>
            <div className="detail-item">
              <label>Validation Date:</label>
              <span>{reportExperiment.validation_timestamp ? formatDate(reportExperiment.validation_timestamp) : '-'}</span>
            </div>
          </div>
        </section>
      </div>

      {/* Full-width sections */}
      <div className="detail-content-full">
        {/* Content Analysis */}
        {reportExperiment.extracted_results && (
          <section className="detail-section">
            <h3><span className="material-icons">description</span> Content Analysis</h3>
            <div className="detail-grid-wide">
              {reportExperiment.extracted_results.plan && (
                <div className="detail-item-wide">
                  <label>Analysis Plan:</label>
                  <div className="detail-text-content">{reportExperiment.extracted_results.plan}</div>
                </div>
              )}
              {reportExperiment.extracted_results.short_description && (
                <div className="detail-item-wide">
                  <label>Short Description:</label>
                  <div className="detail-text-content">{reportExperiment.extracted_results.short_description}</div>
                </div>
              )}
              {reportExperiment.extracted_results.reasoning && Array.isArray(reportExperiment.extracted_results.reasoning) && (
                <div className="detail-item-wide">
                  <label>Reasoning:</label>
                  <div className="detail-text-content">
                    <ul>
                      {reportExperiment.extracted_results.reasoning.map((reason, index) => (
                        <li key={index}>{reason}</li>
                      ))}
                    </ul>
                  </div>
                </div>
              )}
            </div>
          </section>
        )}

        {/* Usage Statistics */}
        {reportExperiment.usage && (
          <section className="detail-section">
            <h3><span className="material-icons">analytics</span> Usage Statistics</h3>
            <div className="detail-grid-metrics">
              <div className="metric-card">
                <label>Total Tokens</label>
                <span className="metric-value">{reportExperiment.usage.total_tokens?.toLocaleString()}</span>
              </div>
              <div className="metric-card">
                <label>Completion Tokens</label>
                <span className="metric-value">{reportExperiment.usage.completion_tokens?.toLocaleString()}</span>
              </div>
              <div className="metric-card">
                <label>Prompt Tokens</label>
                <span className="metric-value">{reportExperiment.usage.prompt_tokens?.toLocaleString()}</span>
              </div>
              {reportExperiment.usage.completion_tokens_details && (
                <>
                  <div className="metric-card">
                    <label>Reasoning Tokens</label>
                    <span className="metric-value">{reportExperiment.usage.completion_tokens_details.reasoning_tokens?.toLocaleString()}</span>
                  </div>
                  <div className="metric-card">
                    <label>Audio Tokens</label>
                    <span className="metric-value">{reportExperiment.usage.completion_tokens_details.audio_tokens?.toLocaleString()}</span>
                  </div>
                </>
              )}
              {reportExperiment.meta && (
                <>
                  <div className="metric-card">
                    <label>Meta Tokens</label>
                    <span className="metric-value">{reportExperiment.meta.num_tokens?.toLocaleString()}</span>
                  </div>
                  <div className="metric-card">
                    <label>Response Cut</label>
                    <span className="metric-value">{reportExperiment.meta.cut ? '❌' : '✅'}</span>
                  </div>
                </>
              )}
            </div>
          </section>
        )}

        {/* Log Probabilities Summary */}
        {reportExperiment.log_probs && reportExperiment.log_probs.length > 0 && (
          <section className="detail-section">
            <h3><span className="material-icons">functions</span> Log Probabilities Summary</h3>
            <div className="detail-grid-wide">
              <div className="detail-item-wide">
                <label>Total Tokens:</label>
                <span>{reportExperiment.log_probs.length}</span>
              </div>
              <div className="detail-item-wide">
                <label>Average Log Probability:</label>
                <span>{(reportExperiment.log_probs.reduce((sum, item) => sum + item.logprob, 0) / reportExperiment.log_probs.length).toFixed(4)}</span>
              </div>
              <div className="detail-item-wide">
                <label>Min Log Probability:</label>
                <span>{Math.min(...reportExperiment.log_probs.map(item => item.logprob)).toFixed(4)}</span>
              </div>
              <div className="detail-item-wide">
                <label>Max Log Probability:</label>
                <span>{Math.max(...reportExperiment.log_probs.map(item => item.logprob)).toFixed(4)}</span>
              </div>
            </div>
            <button className="secondary-button" onClick={() => setShowRaw('log_probs')}>
              View Detailed Log Probabilities
            </button>
          </section>
        )}

        {/* Validation Results */}
        {reportExperiment.validation_results && (
          <section className="detail-section">
            <h3><span className="material-icons">verified</span> Validation Results</h3>
            <div className="property-confidence">
              {Object.entries(reportExperiment.validation_results).map(([field, val]) => (
                <div key={field} className="property-item">
                  <span className="property-name">{field}:</span>
                  <span className="property-value">
                    {typeof val.value === 'object' ? JSON.stringify(val.value) : String(val.value)} 
                    <br />
                    <small>
                      <strong>Confidence:</strong> {(val.confidence * 100).toFixed(1)}% | 
                      <strong> Found:</strong> {val.found ? '✅' : '❌'} | 
                      <strong> Matches:</strong> {val.matches ? val.matches.length : 0}
                    </small>
                  </span>
                </div>
              ))}
            </div>
            <button className="secondary-button" onClick={() => setShowRaw('validation_results')}>
              View Raw Validation Results
            </button>
          </section>
        )}

        {/* Raw Data Actions */}
        <section className="detail-section">
          <h3><span className="material-icons">visibility</span> Raw Data</h3>
          <div className="raw-data-buttons">
            <button className="secondary-button" onClick={() => setShowRaw('full')}>
              View Full Raw Record
            </button>
            {reportExperiment.raw_result && (
              <button className="secondary-button" onClick={() => setShowRaw('raw_result')}>
                View Raw Result
              </button>
            )}
            <button className="secondary-button" onClick={() => setShowRaw('validation_results')}>
              View Validation Results
            </button>
            {reportExperiment.log_probs && (
              <button className="secondary-button" onClick={() => setShowRaw('log_probs')}>
                View Log Probabilities
              </button>
            )}
          </div>
        </section>
      </div>

      {/* Modal for raw data */}
      {showRaw && (
        <div className="modal-overlay" onClick={() => setShowRaw(null)}>
          <div className="modal-content" onClick={e => e.stopPropagation()}>
            <h3>
              {showRaw === 'full' && 'Full Raw Record'}
              {showRaw === 'validation_results' && 'Validation Results'}
              {showRaw === 'log_probs' && 'Log Probabilities'}
              {showRaw === 'raw_result' && 'Raw Result'}
            </h3>
            <pre className="json-text" style={{ maxHeight: '70vh', overflowY: 'auto' }}>
              {showRaw === 'full' && JSON.stringify(reportExperiment, null, 2)}
              {showRaw === 'validation_results' && JSON.stringify(reportExperiment.validation_results, null, 2)}
              {showRaw === 'log_probs' && JSON.stringify(reportExperiment.log_probs, null, 2)}
              {showRaw === 'raw_result' && reportExperiment.raw_result}
            </pre>
            <button className="secondary-button" onClick={() => setShowRaw(null)}>
              {t('buttons.close')}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default ReportExperimentsView; 